import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentMethod, StripeCardElement, StripeCardElementChangeEvent, StripeElementStyle } from '@stripe/stripe-js';
import { Paragraph } from 'components/Typography';
import { getCustomErrorMessage, getIsDeclinedPayment } from './utils';
import { FormField } from '@expressable/syntax-ds';

const cardElementStyle: StripeElementStyle = {
  base: {
    fontSize: '16px',
    fontFamily: 'Dazzed, Inter, sans-serif',
    fontWeight: '500',
    lineHeight: '56px',
    backgroundColor: '#FFFFFF',
  },
  invalid: {
    color: '#9B0600',
    iconColor: '#9B0600',
  },
};

type Props = {
  errorStripeCode?: string;
  errorStripeDeclineCode?: string;
  errorMessage?: string;
  onCardChange?: () => void;
  setIsLoading?: (isLoading: boolean) => void;
  setPaymentMethod: (paymentMethod: PaymentMethod | undefined) => void;
  statusCode?: number;
  disabled?: boolean;

  /** Note(juan): Temporary solution to allow the component to work in the two contexts we have as of today. Setting this value
   * will make the component handle errors via the FormField component from Syntax
   */
  rhfName?: string;
  label?: string;
};

export const AppointmentPaymentInput: React.FC<Props> = ({
  errorStripeCode,
  errorStripeDeclineCode,
  errorMessage,
  onCardChange,
  setIsLoading,
  setPaymentMethod,
  statusCode,
  rhfName,
  label,
  disabled,
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [stripeComplete, setStripeComplete] = useState<boolean | undefined>();
  const [localErrorMessage, setLocalErrorMessage] = useState<string>();

  const handleOnFocus = useCallback(() => {
    if (stripeComplete === false && !localErrorMessage) {
      setLocalErrorMessage('');
    }
  }, [localErrorMessage, stripeComplete]);

  const handleOnBlur = useCallback(() => {
    if (stripeComplete === false && !localErrorMessage) {
      setLocalErrorMessage('Your card information is incomplete.');
    }
  }, [localErrorMessage, stripeComplete]);

  const handleOnChange = useCallback(
    async (event: StripeCardElementChangeEvent) => {
      setLocalErrorMessage('');
      onCardChange?.();

      if (!stripe || !elements) {
        return;
      }

      if (event.complete && !event.error) {
        setIsLoading?.(true);
        setStripeComplete(true);
        const cardElement = elements.getElement(CardElement);
        const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement as StripeCardElement,
        });

        if (stripeError) {
          setLocalErrorMessage(stripeError.message);
          setPaymentMethod(undefined);
        } else {
          setPaymentMethod(paymentMethod);
        }

        setIsLoading?.(false);
        return;
      } else if (event.error && 'message' in event.error) {
        setLocalErrorMessage(event.error.message);
      } else if (!event.complete) {
        setStripeComplete(false);
      }

      setPaymentMethod(undefined);
    },
    [elements, setLocalErrorMessage, setIsLoading, setPaymentMethod, stripe],
  );

  const customErrorMessage = getCustomErrorMessage(errorMessage || localErrorMessage, errorStripeCode, statusCode);
  const isDeclinedPayment = getIsDeclinedPayment(errorStripeDeclineCode, errorStripeCode);

  const errorTexts = useMemo(() => {
    const errors = [];

    if (customErrorMessage) {
      errors.push(customErrorMessage);
    }

    if (isDeclinedPayment) {
      errors.push(t('appointmentPayment.error.label'));
    }

    return errors;
  }, [customErrorMessage, isDeclinedPayment]);

  const cardComponent = (
    <CardElement
      options={{ style: cardElementStyle, disabled }}
      className={`mb-4 pl-16 pr-8 bg-white border rounded-md ${isDeclinedPayment ? 'border-error-900' : ''}`}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      onFocus={handleOnFocus}
    />
  );

  if (rhfName) {
    return (
      // @TODO(juan): workaround to prevent an empty error array from hiding rhf-internal errors (that come from validation). Will be fixed in syntax
      <FormField label={label} name={rhfName} {...(errorTexts.length > 0 ? { errorMessages: errorTexts } : {})}>
        {cardComponent}
      </FormField>
    );
  }

  return (
    <>
      {cardComponent}

      {isDeclinedPayment ? (
        <span className="w-full text-sm text-error-900">{t('appointmentPayment.error.label')}</span>
      ) : null}

      {customErrorMessage ? (
        <Paragraph className="w-full mt-40 font-semibold text-error-900">{customErrorMessage}</Paragraph>
      ) : null}
    </>
  );
};
